import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createCadenceTasks,
  deleteCadenceTask,
  fetchCadenceMonth,
  fetchLeadCadence,
  fetchTodayCadence,
  updateCadenceTask,
  type CadenceDaySummary,
  type CadenceStatus,
  type CadenceTask,
} from "../api/cadence.ts";
import { useAppActions } from "../app/AppProvider.tsx";
import { useToast } from "../components/ToastProvider.tsx";
import type { Lead } from "../types.ts";
import {
  buildCalendarGrid,
  filterCadenceTasks,
  isoMonth,
  tasksForDate,
  todayIsoDate,
  type CadenceTaskFilters,
} from "./CadenceCalendar.helpers.ts";
import { openChatPatchForLead, preferredWhatsappAccount } from "./Kanban.helpers.ts";
import { CalendarGrid } from "./cadence-calendar/CalendarGrid.tsx";
import { CadenceFilters } from "./cadence-calendar/CadenceFilters.tsx";
import { CadenceHeader } from "./cadence-calendar/CadenceHeader.tsx";
import { CadenceMetrics } from "./cadence-calendar/CadenceMetrics.tsx";
import { DayPanel } from "./cadence-calendar/DayPanel.tsx";
import { emitCadenceTodayRefresh } from "./cadence-calendar/events.ts";
import { ScheduleDialog, type CadenceScheduleSubmit } from "./cadence-calendar/ScheduleDialog.tsx";
import { TodayAlert } from "./cadence-calendar/TodayAlert.tsx";
export function CadenceCalendar() {
  const { state, patch } = useAppActions();
  const { showToast } = useToast();
  const [month, setMonth] = useState(() => isoMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState(() => todayIsoDate());
  const [monthDays, setMonthDays] = useState<CadenceDaySummary[]>([]);
  const [todaySummary, setTodaySummary] = useState<CadenceDaySummary | null>(null);
  const [filters, setFilters] = useState<CadenceTaskFilters>({ funnelId: "", stage: "", priority: "", campaign: "" });
  const [schedulerOpen, setSchedulerOpen] = useState(false);
  const [rescheduleTask, setRescheduleTask] = useState<CadenceTask | null>(null);
  const [historyLeadId, setHistoryLeadId] = useState("");
  const [historyTasks, setHistoryTasks] = useState<CadenceTask[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyTaskId, setBusyTaskId] = useState<number | null>(null);
  const [error, setError] = useState("");
  const reload = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [nextMonth, nextToday] = await Promise.all([
        fetchCadenceMonth(month),
        fetchTodayCadence(),
      ]);
      setMonthDays(nextMonth.days);
      setTodaySummary(nextToday);
      emitCadenceTodayRefresh(nextToday);
      return nextToday;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Falha ao carregar cadencias.";
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    void reload();
  }, [reload]);
  const selectedTasks = useMemo(() => tasksForDate(monthDays, selectedDate), [monthDays, selectedDate]);
  const filteredSelectedTasks = useMemo(
    () => filterCadenceTasks(selectedTasks, state.leads, filters),
    [filters, selectedTasks, state.leads],
  );
  const todayTasks = useMemo(
    () => filterCadenceTasks(todaySummary?.tasks || [], state.leads, filters),
    [filters, state.leads, todaySummary],
  );
  const cells = useMemo(() => buildCalendarGrid(month, monthDays), [month, monthDays]);
  const todayPending = Number(todaySummary?.pending || 0);
  const schedulerInitialLeadIds = useMemo(() => (
    rescheduleTask
      ? [String(rescheduleTask.leadId)]
      : state.cadenceDraftLeadId
        ? [state.cadenceDraftLeadId]
        : []
  ), [rescheduleTask, state.cadenceDraftLeadId]);
  const selectedHistoryLeadName = historyLeadId
    ? state.leads.find((lead) => String(lead.id) === historyLeadId)?.name || historyTasks[0]?.leadName || historyLeadId
    : "";

  useEffect(() => {
    if (!state.cadenceSchedulerOpen) return;
    setRescheduleTask(null);
    setSchedulerOpen(true);
    patch({ cadenceSchedulerOpen: false });
  }, [patch, state.cadenceSchedulerOpen]);

  const selectToday = useCallback(() => {
    const today = todayIsoDate();
    setSelectedDate(today);
    setMonth(isoMonth(new Date()));
  }, []);

  const closeScheduler = useCallback(() => {
    setSchedulerOpen(false);
    setRescheduleTask(null);
    patch({ cadenceDraftLeadId: "", cadenceSchedulerOpen: false });
  }, [patch]);

  const submitCadence = useCallback(async (payload: CadenceScheduleSubmit) => {
    if (!payload.leadIds.length) {
      showToast("Selecione pelo menos um lead.", "error");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (rescheduleTask) {
        await updateCadenceTask(rescheduleTask.id, {
          scheduledDate: payload.dates[0] || payload.startDate || selectedDate,
          scheduledPeriod: payload.periods[0] || "",
          note: payload.note,
          status: "pending",
        });
        showToast("Cadencia reagendada.", "success");
      } else {
        const payloadBase = payload.startDate && payload.endDate
          ? { startDate: payload.startDate, endDate: payload.endDate }
          : { dates: payload.dates };

        await Promise.all(payload.periods.map((scheduledPeriod) => (
          createCadenceTasks({
            leadIds: payload.leadIds,
            ...payloadBase,
            scheduledPeriod,
            note: payload.note,
          })
        )));
        showToast("Cadencia agendada.", "success");
      }
      closeScheduler();
      await reload();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Falha ao salvar cadencia.";
      setError(message);
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  }, [closeScheduler, reload, rescheduleTask, selectedDate, showToast]);

  const changeTaskStatus = useCallback(async (taskId: number, status: CadenceStatus) => {
    setBusyTaskId(taskId);
    try {
      await updateCadenceTask(taskId, { status });
      await reload();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Falha ao atualizar cadencia.", "error");
    } finally {
      setBusyTaskId(null);
    }
  }, [reload, showToast]);

  const removeTask = useCallback(async (taskId: number) => {
    setBusyTaskId(taskId);
    try {
      await deleteCadenceTask(taskId);
      await reload();
      showToast("Cadencia removida.", "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Falha ao remover cadencia.", "error");
    } finally {
      setBusyTaskId(null);
    }
  }, [reload, showToast]);

  const moveTaskToDate = useCallback(async (taskId: number, scheduledDate: string) => {
    setBusyTaskId(taskId);
    try {
      await updateCadenceTask(taskId, { scheduledDate, status: "pending" });
      await reload();
      showToast("Cadencia reagendada.", "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Falha ao reagendar cadencia.", "error");
    } finally {
      setBusyTaskId(null);
    }
  }, [reload, showToast]);

  const openReschedule = useCallback((task: CadenceTask) => {
    setRescheduleTask(task);
    setSchedulerOpen(true);
  }, []);

  const openLeadHistory = useCallback(async (task: CadenceTask) => {
    const leadId = String(task.leadId);
    setHistoryLeadId(leadId);
    setHistoryLoading(true);
    try {
      setHistoryTasks(await fetchLeadCadence(leadId));
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Falha ao carregar historico.", "error");
      setHistoryTasks([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [showToast]);

  const openConversationForTask = useCallback((task: CadenceTask) => {
    const lead: Lead = state.leads.find((item) => String(item.id) === String(task.leadId)) || {
      id: task.leadId,
      name: task.leadName,
      phone: task.leadPhone,
      funnelId: task.leadFunnelId,
      stage: task.leadStage,
      priority: task.leadPriority,
    };
    const result = openChatPatchForLead(
      lead,
      state.chats,
      preferredWhatsappAccount(state.accounts || [], state.accountFilter || [], state.whatsappSession),
    );
    if (result.error || !result.patch) {
      showToast(result.error || "Nao foi possivel abrir a conversa.", "error");
      return;
    }
    patch(result.patch);
  }, [patch, showToast, state.accountFilter, state.accounts, state.chats, state.leads, state.whatsappSession]);

  return (
    <section className="cadence-shell" aria-label="Calendario de cadencia">
      <CadenceHeader
        month={month}
        loading={loading}
        onMonthChange={setMonth}
        onOpenScheduler={() => setSchedulerOpen(true)}
        onReload={() => void reload()}
      />

      <CadenceMetrics days={monthDays} />

      <TodayAlert pending={todayPending} onSelectToday={selectToday} />

      {error ? <div className="cadence-alert">{error}</div> : null}

      <CadenceFilters filters={filters} funnels={state.funnels} leads={state.leads} onChange={setFilters} />

      <div className="cadence-layout">
        <CalendarGrid
          cells={cells}
          selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
          onDropTask={moveTaskToDate}
        />

        <DayPanel
          selectedDate={selectedDate}
          loading={loading}
          selectedTasks={filteredSelectedTasks}
          todayTasks={todayTasks}
          showTodayTasks={todayTasks.length > 0 && selectedDate !== todayIsoDate()}
          busyTaskId={busyTaskId}
          historyLeadId={historyLeadId}
          historyLeadName={selectedHistoryLeadName}
          historyTasks={historyTasks}
          historyLoading={historyLoading}
          onStatus={changeTaskStatus}
          onOpenConversation={openConversationForTask}
          onReschedule={openReschedule}
          onHistory={(task) => void openLeadHistory(task)}
          onDelete={removeTask}
          onCloseHistory={() => {
            setHistoryLeadId("");
            setHistoryTasks([]);
          }}
        />
      </div>

      <ScheduleDialog
        open={schedulerOpen}
        mode={rescheduleTask ? "reschedule" : "create"}
        leads={state.leads}
        selectedDayTasks={selectedTasks}
        initialLeadIds={schedulerInitialLeadIds}
        initialDate={rescheduleTask?.scheduledDate || selectedDate}
        initialPeriod={rescheduleTask?.scheduledPeriod || ""}
        initialNote={rescheduleTask?.note || ""}
        saving={saving}
        onClose={closeScheduler}
        onSubmit={(payload) => void submitCadence(payload)}
      />
    </section>
  );
}
